import { useState, useEffect, useCallback } from 'react';
import {
  SalesOrder,
  SalesOrderPayment,
  SalesOrderInstallment,
} from '../../../../types/sales.types';
import {
  SalesOrderPayment as APISalesOrderPayment,
  SalesOrderInstallment as APISalesOrderInstallment,
} from '../../../../api';
import { salesService } from '../../../../services/sales.service';
import { SALES_ORDER_STATUS_CONFIG, PAYMENT_METHOD_LABELS } from '../../../../utils/constants';
import { hasLineMarkup } from '../../../../utils/sales/line-pricing.utils';
import { mapApiPaymentToLocal } from '../../../../utils/sales/mapApiPaymentToLocal';

interface UseOrderDetailsModalProps {
  open: boolean;
  orderId: number | null;
  onEditOrder?: (orderId: number) => void;
  onProcessPayment?: (orderId: number) => void;
  onGenerateInstallments?: (orderId: number) => void;
  onPrintReceipt?: (orderId: number) => void;
  onCancelOrder?: (orderId: number) => void;
}

export const useOrderDetailsModal = ({
  open,
  orderId,
  onEditOrder,
  onProcessPayment,
  onGenerateInstallments,
  onPrintReceipt,
  onCancelOrder,
}: UseOrderDetailsModalProps) => {
  const [order, setOrder] = useState<SalesOrder | null>(null);
  const [payments, setPayments] = useState<SalesOrderPayment[]>([]);
  const [installments, setInstallments] = useState<SalesOrderInstallment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapAPIInstallmentToLocal = useCallback(
    (
      apiInstallment: APISalesOrderInstallment,
      currentOrder: SalesOrder,
    ): SalesOrderInstallment => ({
      id: apiInstallment.id,
      order: currentOrder,
      payment: undefined,
      installmentNumber: apiInstallment.installmentNumber,
      installmentTotal: apiInstallment.installmentTotal,
      dueDate: new Date(apiInstallment.dueDate),
      amount: apiInstallment.amount,
      status: (['PENDING', 'PAID', 'OVERDUE', 'CANCELLED'] as const).includes(
        apiInstallment.status as SalesOrderInstallment['status'],
      )
        ? (apiInstallment.status as SalesOrderInstallment['status'])
        : 'PENDING',
      paidDate: apiInstallment.paidDate ? new Date(apiInstallment.paidDate) : undefined,
      lateFee: apiInstallment.lateFee,
      notes: apiInstallment.notes,
      createdAt: new Date(apiInstallment.createdAt),
    }),
    [],
  );

  // Load order details
  const loadOrderDetails = useCallback(async () => {
    if (orderId == null) return;

    setIsLoading(true);
    setError(null);

    try {
      const orderDetails = await salesService.getSalesOrderComplete(orderId);
      setOrder(orderDetails);

      // Complete joins payments — use them; fall back only if the payload omitted payments.
      const paymentsWereIncluded = Array.isArray(orderDetails.payments);
      const paymentsPromise = paymentsWereIncluded
        ? Promise.resolve(orderDetails.payments)
        : salesService
            .getPaymentsByOrder(orderId)
            .then((orderPayments) =>
              orderPayments.map((p: APISalesOrderPayment) => mapApiPaymentToLocal(p, orderDetails)),
            );

      // Installments in parallel with payments (not after).
      const installmentsPromise = orderDetails.hasInstallments
        ? salesService
            .getInstallmentsByOrder(orderId)
            .then((orderInstallments) =>
              orderInstallments.map((ins: APISalesOrderInstallment) =>
                mapAPIInstallmentToLocal(ins, orderDetails),
              ),
            )
        : Promise.resolve([] as SalesOrderInstallment[]);

      const [mappedPayments, mappedInstallments] = await Promise.all([
        paymentsPromise,
        installmentsPromise,
      ]);
      setPayments(mappedPayments);
      setInstallments(mappedInstallments);
    } catch (err: unknown) {
      console.error('Error loading order details:', err);
      setError('Error al cargar los detalles de la venta');
    } finally {
      setIsLoading(false);
    }
  }, [orderId, mapAPIInstallmentToLocal]);

  useEffect(() => {
    if (open && orderId != null) {
      loadOrderDetails();
    }
  }, [open, orderId, loadOrderDetails]);

  // Refresh order details
  const refreshOrderDetails = useCallback(() => {
    if (orderId != null) {
      loadOrderDetails();
    }
  }, [orderId, loadOrderDetails]);

  // Action handler
  const handleAction = useCallback(
    (action: string) => {
      if (order == null) return;

      switch (action) {
        case 'edit':
          onEditOrder?.(order.id);
          break;
        case 'payment':
          onProcessPayment?.(order.id);
          break;
        case 'installments':
          onGenerateInstallments?.(order.id);
          break;
        case 'print':
          onPrintReceipt?.(order.id);
          break;
        case 'cancel':
          onCancelOrder?.(order.id);
          break;
      }
    },
    [order, onEditOrder, onProcessPayment, onGenerateInstallments, onPrintReceipt, onCancelOrder],
  );

  // Get status chip config
  const getStatusChip = useCallback((status: string) => {
    return (
      SALES_ORDER_STATUS_CONFIG[status as keyof typeof SALES_ORDER_STATUS_CONFIG] ??
      SALES_ORDER_STATUS_CONFIG['DRAFT']
    );
  }, []);

  // Get payment method label
  const getPaymentMethodLabel = useCallback((method: string) => {
    return PAYMENT_METHOD_LABELS[method as keyof typeof PAYMENT_METHOD_LABELS] ?? method;
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('es-BO', {
      style: 'currency',
      currency: 'BOB',
    }).format(amount);
  }, []);

  // Format date
  const formatDate = useCallback((date: Date | string) => {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Calculate financial dashboard values
  const calculateFinancialDashboard = useCallback(() => {
    if (!order) return null;

    const orderTotal = Number((order.totalAmount ?? order.totalWithTax ?? 0) as any) || 0;

    // Separate regular payments from refunds
    const regularPayments = payments.filter(
      (p) => p.status === 'COMPLETED' && Number(p.paymentAmount) > 0,
    );
    const refundPayments = payments.filter(
      (p) => p.status === 'COMPLETED' && Number(p.paymentAmount) < 0,
    );

    const totalPaidRaw = regularPayments.reduce(
      (sum, p) => sum + (Number((p.paymentAmount as any) ?? 0) || 0),
      0,
    );
    const totalRefundedRaw = Math.abs(
      refundPayments.reduce((sum, p) => sum + (Number((p.paymentAmount as any) ?? 0) || 0), 0),
    );

    const totalPaid = Number.isFinite(totalPaidRaw) ? totalPaidRaw : 0;
    const totalRefunded = Number.isFinite(totalRefundedRaw) ? totalRefundedRaw : 0;
    const netPaid = totalPaid - totalRefunded;

    const remainingCalc = orderTotal - netPaid;
    const remaining = Number.isFinite(remainingCalc) ? Math.max(remainingCalc, 0) : 0;
    const hasRefunds = refundPayments.length > 0;

    return {
      orderTotal,
      totalPaid,
      totalRefunded,
      netPaid,
      remaining,
      hasRefunds,
      regularPayments,
      refundPayments,
    };
  }, [order, payments]);

  // Calculate discount for a product
  const calculateProductDiscount = useCallback((item: any) => {
    const basePrice = Number(item.adjustedPrice ?? item.originalPrice);
    const discountAmount =
      item.discountValue != null && item.discountValue > 0
        ? item.discountType === 'PERCENTAGE'
          ? (basePrice * (Number(item.discountValue) || 0)) / 100
          : Number(item.discountValue)
        : 0;
    return discountAmount;
  }, []);

  const formatDiscountDisplay = useCallback(
    (discountType: string, discountValue: number, basePrice: number) => {
      if (discountType === 'PERCENTAGE') {
        const equivalentAmount = basePrice * (discountValue / 100);
        return `${discountValue}% (${formatCurrency(equivalentAmount)})`;
      }
      return formatCurrency(discountValue);
    },
    [formatCurrency],
  );

  const formatMarkupDisplay = useCallback(
    (markupType: string, markupValue: number, originalPrice: number) => {
      if (markupType === 'PERCENTAGE') {
        const equivalentAmount = originalPrice * (markupValue / 100);
        return `${markupValue}% (${formatCurrency(equivalentAmount)})`;
      }
      return formatCurrency(markupValue);
    },
    [formatCurrency],
  );

  const hasProductMarkup = useCallback(
    (item: { markupType?: string; markupValue?: number }) =>
      hasLineMarkup(
        (item.markupType ?? 'NONE') as 'PERCENTAGE' | 'AMOUNT' | 'NONE',
        Number(item.markupValue ?? 0),
      ),
    [],
  );

  return {
    // State
    order,
    payments,
    installments,
    isLoading,
    error,
    // Handlers
    handleAction,
    refreshOrderDetails,
    // Helpers
    getStatusChip,
    getPaymentMethodLabel,
    formatCurrency,
    formatDate,
    calculateFinancialDashboard,
    calculateProductDiscount,
    formatDiscountDisplay,
    formatMarkupDisplay,
    hasProductMarkup,
  };
};
